// Libraries
import { useState } from 'react';
import { Flex, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react';
// Components
import { ColumnFilterProps } from '@organisms/index';
import { DebouncedNumericInput } from '@molecules/index';
// Utils
import { useUnitAndRangeStore } from '@stores/index';
import { minMaxFromArray } from '@helpers/index';
import text from '@translations/en.json';
import { customSort, getAllUnitsWithCapacities, getOptionsInRange } from './helpers';
// Types
import { UnitAndRangeFilterProps } from './UnitAndRangeFilter.types';

export const UnitAndRangeFilter = <TableData,>({ column }: ColumnFilterProps<TableData, UnitAndRangeFilterProps>) => {
  const { minValue, maxValue, unit, setUnit, setMinValue, setMaxValue, setValues, setRange, clear, setOptions } =
    useUnitAndRangeStore();
  const [minError, setMinError] = useState('');
  const [maxError, setMaxError] = useState('');
  const { options, setFilterValue } = column;
  const initialUnitsWithCapacities = getAllUnitsWithCapacities(options as string[]);
  const radioOptions = customSort(Object.keys(initialUnitsWithCapacities), 'All');
  const radioValue = unit;
  const [minRange, maxRange] = minMaxFromArray(initialUnitsWithCapacities[radioValue]);
  const showRange = radioValue !== 'All';
  const optionsPerUnit = initialUnitsWithCapacities[radioValue].map((capacity) => `${capacity}/${radioValue}`);

  const handleChange = (newRadioValue: string) => {
    setValues(undefined, undefined);
    setUnit(newRadioValue);
    if (newRadioValue === 'All') {
      clear();
      setFilterValue(undefined);
    } else {
      const newCapacities = initialUnitsWithCapacities[newRadioValue];
      const newOptions = newCapacities.map((capacity) => `${capacity}/${newRadioValue}`);
      const newRange = minMaxFromArray(newCapacities);
      setRange(newRange);
      setOptions(newOptions);
      setFilterValue(newOptions);
    }
  };

  const handleMinChange = (newMinValue: number) => {
    const newOptions = getOptionsInRange(optionsPerUnit, newMinValue, maxValue ?? maxRange);
    if (newMinValue && maxValue && newMinValue >= maxValue) {
      setMinError('Must be less than max value');
    } else if (newMinValue && newMinValue !== minValue) {
      setMinError('');
      setMinValue(newMinValue);
      setFilterValue(newOptions);
    }
  };

  const handleMaxChange = (newMaxValue: number) => {
    const newOptions = getOptionsInRange(optionsPerUnit, minValue ?? minRange, newMaxValue);
    if (newMaxValue && minValue && newMaxValue <= minValue) {
      setMaxError('Must be more than min value');
    } else if (newMaxValue && newMaxValue >= minRange) {
      setMaxError('');
      setMaxValue(newMaxValue);
      setFilterValue(newOptions);
    }
  };

  return (
    <Flex direction='column' bg='table.footer' borderBottom='section' p={4} gap={4}>
      <Text fontSize='sm' fontWeight='medium'>
        {text.capacity_range}
      </Text>
      <RadioGroup onChange={handleChange} value={radioValue}>
        <Stack direction='row'>
          {radioOptions.map((radioOption) => (
            <Radio key={radioOption} value={radioOption}>
              <Text fontSize='sm' ml={-1}>
                {radioOption}
              </Text>
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
      {showRange && (
        <Flex gap={4}>
          <Flex direction='column'>
            <DebouncedNumericInput
              label='Min'
              min={minRange}
              max={maxRange}
              step={500}
              defaultValue={minRange}
              value={minValue ?? minRange}
              onChange={handleMinChange}
              error={minError}
            />
          </Flex>
          <Flex direction='column'>
            <DebouncedNumericInput
              label='Max'
              min={minRange}
              max={maxRange}
              step={500}
              defaultValue={maxRange}
              value={maxValue ?? maxRange}
              onChange={handleMaxChange}
              error={maxError}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
